import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './auth.store';
import { loginSchema, type LoginForm } from '@/shared/schemas/auth.schema';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { ApiException } from '@/shared/api/client';

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    try {
      await login(data.email, data.password);
      navigate('/', { replace: true });
    } catch (e) {
      setServerError(e instanceof ApiException ? e.message : 'Anmeldung fehlgeschlagen.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-brand-700">ProofFlow</h1>
          <p className="mt-1 text-sm text-slate-500">Dokumentieren. Bestätigen. Abrechnen.</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 rounded-xl bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Anmelden</h2>
          {serverError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{serverError}</div>
          )}
          <Input
            label="E-Mail"
            type="email"
            autoComplete="email"
            {...register('email')}
            error={errors.email?.message}
          />
          <Input
            label="Passwort"
            type="password"
            autoComplete="current-password"
            {...register('password')}
            error={errors.password?.message}
          />
          <Button type="submit" loading={isSubmitting} className="w-full">
            Anmelden
          </Button>
          <p className="text-center text-sm text-slate-500">
            Noch kein Konto?{' '}
            <Link to="/registrieren" className="font-medium text-brand-600 hover:underline">
              Betrieb registrieren
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
